import { useCallback, useState } from 'react';
import { withCarriedSlotHotkey } from '@sorrem-client/ui/shared/action-bar';
import { useActionBarTargets, useInputBindingPress } from '@sorrem-client/ui/hooks';

export interface CarriedSlotHotkeyAssignment {
    /** The carried slot waiting for the player to pick its action slot, or null. */
    readonly linkingSlotIndex: number | null;
    /** Starts (with a slot index) or cancels (null) picking an action slot for a carried slot. */
    readonly setLinkingSlotIndex: (slotIndex: number | null) => void;
    /** Links the waiting carried slot to hotkey `hotkeyIndex`, or unlinks it if it is already there. */
    readonly link: (hotkeyIndex: number) => void;
}

/**
 * The Inventory tab's carried-slot linking, as in the run's Carried panel:
 * clicking a carried slot's key label waits for an action slot, and the
 * player clicks one (or presses its key) to link the slot there. A slot has
 * one hotkey, and picking its current one again unlinks it. Esc cancels, as
 * does leaving the tab.
 *
 * @param isActive whether the Inventory tab is open.
 */
export function useCarriedSlotHotkeyAssignment(isActive: boolean): CarriedSlotHotkeyAssignment {
    const { targets, setTargets } = useActionBarTargets();
    const [linkingSlotIndex, setLinking] = useState<number | null>(null);

    // Leaving the tab ends the mode.
    if (linkingSlotIndex !== null && !isActive) {
        setLinking(null);
    }

    const setLinkingSlotIndex = useCallback((slotIndex: number | null): void => {
        // The page input ignores keys while a button has focus, and the
        // action-bar key that picks the slot must reach it.
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setLinking(slotIndex);
    }, []);

    const link = useCallback((hotkeyIndex: number): void => {
        if (linkingSlotIndex === null) return;
        setTargets(withCarriedSlotHotkey(targets, hotkeyIndex, linkingSlotIndex));
        setLinking(null);
    }, [linkingSlotIndex, setTargets, targets]);

    useInputBindingPress('open-menu', linkingSlotIndex === null ? undefined : (): void => setLinking(null));

    return { linkingSlotIndex, setLinkingSlotIndex, link };
}
